import React from 'react';
import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material';
import { useBookingCopyTranslation as useTranslation } from '../../i18n/bookingCopy';
import { TutorBookingChangeProposal, TutorBookingChangeStatus } from '../../models/TutorBookingChange';
import {
  formatProposalDateTimeWithDuration,
  getProposalOldEndIso,
  getTutorChangeProposalReason,
  getTutorChangeProposalStatusColor,
  getTutorChangeProposalStatusDescription,
  getUserTutorChangeProposalStatusLabel,
} from '../../utils/tutorBookingChangeProposal';

export interface TutorBookingChangeProposalScheduleChipProps {
  proposal: TutorBookingChangeProposal;
  bookingStartTime: string;
  bookingEndTime: string;
  locale: string;
  audience: 'host' | 'user';
  timezone?: string | null;
  /** When false, skip previous time in the tooltip (e.g. already shown struck-through inline). */
  showPreviousTimeInTooltip?: boolean;
}

export default function TutorBookingChangeProposalScheduleChip({
  proposal,
  bookingStartTime,
  bookingEndTime,
  locale,
  audience,
  timezone,
  showPreviousTimeInTooltip = true,
}: TutorBookingChangeProposalScheduleChipProps) {
  const { t } = useTranslation();
  const pending = proposal.status === TutorBookingChangeStatus.PENDING_REPLY;

  const label =
    pending && audience === 'host'
      ? t('hostTutorBookings.changeProposal.awaitingStudentStatus', 'Awaiting student')
      : audience === 'user'
        ? getUserTutorChangeProposalStatusLabel(proposal.status, t)
        : getTutorChangeProposalStatusDescription(proposal.status, t);

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (hours === 0) return `${remainingMinutes} ${t('roomBookingRequests.min')}`;
    if (remainingMinutes === 0) {
      return `${hours} ${hours > 1 ? t('roomBookingRequests.hrs') : t('roomBookingRequests.hr')}`;
    }
    return `${hours} ${hours > 1 ? t('roomBookingRequests.hrs') : t('roomBookingRequests.hr')} ${remainingMinutes} ${t('roomBookingRequests.min')}`;
  };

  const reason = getTutorChangeProposalReason(proposal);
  const showPrevious = showPreviousTimeInTooltip;

  let tooltip: React.ReactNode = '';
  if (showPrevious || reason) {
    const previousText = showPrevious
      ? formatProposalDateTimeWithDuration(
          proposal.oldStartTime,
          getProposalOldEndIso(proposal, bookingStartTime, bookingEndTime),
          locale,
          formatDuration,
          timezone,
        )
      : null;

    tooltip = (
      <Stack spacing={0.5} sx={{ py: 0.25, maxWidth: 280 }}>
        {previousText ? (
          <Box>
            <Typography variant="caption" component="div" sx={{ opacity: 0.8 }}>
              {t('bookings.changeProposalScheduleChip.previousTime', 'Previous time')}
            </Typography>
            <Typography variant="caption" component="div" fontWeight={600}>
              {previousText}
            </Typography>
          </Box>
        ) : null}
        {reason ? (
          <Box>
            <Typography variant="caption" component="div" sx={{ opacity: 0.8 }}>
              {t('bookings.changeProposal.proposalReason', 'Proposal reason')}
            </Typography>
            <Typography variant="caption" component="div" fontWeight={600}>
              {reason}
            </Typography>
          </Box>
        ) : null}
      </Stack>
    );
  }

  const chip = (
    <Chip
      size="small"
      variant={pending ? 'filled' : 'outlined'}
      color={pending ? 'warning' : getTutorChangeProposalStatusColor(proposal.status)}
      label={label}
      sx={{
        fontWeight: 600,
        flexShrink: 0,
        height: 20,
        fontSize: '0.7rem',
        '& .MuiChip-label': { px: 0.85 },
      }}
    />
  );

  if (!tooltip) return chip;

  return (
    <Tooltip title={tooltip} arrow enterDelay={200} describeChild>
      {chip}
    </Tooltip>
  );
}
